import type { GameStatus } from '@volley/crucible-ipc-types/ipc-channels';

export function formatTimeAgo(isoDate?: string): string {
  if (!isoDate) return '';
  const diff = Date.now() - new Date(isoDate).getTime();
  if (isNaN(diff) || diff < 0) return isoDate;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString();
}

export const PHASE_STYLE: Record<string, string> = {
  Running: 'bg-green-500/20 text-green-400',
  Building: 'bg-yellow-500/20 text-yellow-400',
  Pending: 'bg-yellow-500/20 text-yellow-400',
  Failed: 'bg-red-500/20 text-red-400',
};

const CI_STYLE: Record<string, string> = {
  success: 'bg-green-500/20 text-green-400',
  failure: 'bg-red-500/20 text-red-400',
  in_progress: 'bg-blue-500/20 text-blue-400',
  queued: 'bg-white/10 text-white/50',
};

/**
 * Displays detailed game status: local git, Bifrost prototype, Registry, and CI.
 * Mirrors the `crucible status <gameId>` CLI output.
 */
export function GameStatusPanel({ status }: { status: GameStatus }): React.JSX.Element {
  const { localGit, bifrost, registry, ci } = status;

  return (
    <div className="grid grid-cols-2 gap-3 text-xs">
      {/* Local Git */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <h4 className="mb-1.5 font-medium text-white/50">Local Git</h4>
        {localGit.exists ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-white/70">{localGit.branch ?? 'unknown'}</span>
              {localGit.uncommittedChanges === 0 ? (
                <span className="rounded-full bg-green-500/20 px-1.5 py-0.5 text-green-400">clean</span>
              ) : (
                <span className="rounded-full bg-yellow-500/20 px-1.5 py-0.5 text-yellow-400">
                  {localGit.uncommittedChanges} uncommitted
                </span>
              )}
            </div>
            {localGit.lastCommitMessage && (
              <p className="truncate text-white/40" title={localGit.lastCommitMessage}>
                {localGit.lastCommitMessage}
              </p>
            )}
            {localGit.lastCommitDate && (
              <p className="text-white/30">{formatTimeAgo(localGit.lastCommitDate)}</p>
            )}
          </div>
        ) : (
          <p className="text-white/30">Not a git repository</p>
        )}
      </div>

      {/* Bifrost Prototype */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <h4 className="mb-1.5 font-medium text-white/50">Prototype</h4>
        {bifrost.exists ? (
          <div className="space-y-1">
            <span className={`inline-block rounded-full px-1.5 py-0.5 font-medium ${PHASE_STYLE[bifrost.phase ?? ''] ?? 'bg-white/10 text-white/50'}`}>
              {bifrost.phase ?? 'Unknown'}
            </span>
            {bifrost.hostname && (
              <p className="truncate text-white/40" title={bifrost.hostname}>{bifrost.hostname}</p>
            )}
            {bifrost.uptime && <p className="text-white/30">Up {bifrost.uptime}</p>}
          </div>
        ) : (
          <p className="text-white/30">Not deployed</p>
        )}
      </div>

      {/* Registry — only shown when published */}
      {registry.exists && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-3">
          <h4 className="mb-1.5 font-medium text-white/50">Published</h4>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-white/70">{registry.version}</span>
              {registry.author && <span className="text-white/40">{registry.author}</span>}
            </div>
            {registry.lastUpdated && (
              <p className="text-white/30">{formatTimeAgo(registry.lastUpdated)}</p>
            )}
          </div>
        </div>
      )}

      {/* CI — only shown when there are runs */}
      {ci.exists && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-3">
          <h4 className="mb-1.5 font-medium text-white/50">CI</h4>
          <div className="space-y-1">
            <span className={`inline-block rounded-full px-1.5 py-0.5 font-medium ${CI_STYLE[ci.conclusion ?? ci.status ?? ''] ?? 'bg-white/10 text-white/50'}`}>
              {ci.conclusion ?? ci.status ?? 'Unknown'}
            </span>
            {ci.url && (
              <button
                onClick={(e) => { e.stopPropagation(); window.open(ci.url, '_blank'); }}
                className="block text-crucible-accent hover:underline"
              >
                View run
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
